import * as React from 'react';
import { useQuery } from '@tanstack/react-query';

import ENDPOINTS from 'shared/api/endpoints';
import RequestClient from 'shared/api/http-client';

type ServerTimeResponse = {
  current_timestamp: number;
};

function normalizeToMs(ts: number): number {
  // If server returns seconds, convert to ms.
  // 1e12 ~= 2001-09-09 in ms; modern ms timestamps are > 1e12.
  return ts < 1_000_000_000_000 ? ts * 1000 : ts;
}

async function fetchServerTimeMs(): Promise<number> {
  const res = await RequestClient.get(ENDPOINTS.SERVER_TIME);
  const raw = (res?.data as ServerTimeResponse | undefined)?.current_timestamp;
  if (typeof raw !== 'number' || Number.isNaN(raw)) {
    throw new Error('Invalid server time response');
  }
  return normalizeToMs(raw);
}

// Singleton “server now” store to avoid per-component intervals.
const store = {
  listeners: new Set<() => void>(),
  timer: null as ReturnType<typeof setInterval> | null,
  nowMs: Date.now(),
  baseServerMs: null as number | null,
  baseClientMs: Date.now(),
  visibilityListenerAdded: false,
};

function computeNowMs() {
  if (store.baseServerMs == null) return Date.now();
  return store.baseServerMs + (Date.now() - store.baseClientMs);
}

function emit() {
  for (const l of store.listeners) l();
}

function start() {
  if (store.timer) return;
  store.timer = setInterval(() => {
    store.nowMs = computeNowMs();
    emit();
  }, 500);
  if (!store.visibilityListenerAdded && typeof document !== 'undefined') {
    store.visibilityListenerAdded = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        store.nowMs = computeNowMs();
        emit();
        start();
      } else {
        if (store.timer) {
          clearInterval(store.timer);
          store.timer = null;
        }
      }
    });
  }
}

function stop() {
  if (!store.timer) return;
  clearInterval(store.timer);
  store.timer = null;
}

function subscribe(cb: () => void) {
  store.listeners.add(cb);
  start();
  return () => {
    store.listeners.delete(cb);
    if (store.listeners.size === 0) stop();
  };
}

function getSnapshot() {
  return store.nowMs;
}

export function setBase(serverMs: number) {
  store.baseServerMs = serverMs;
  store.baseClientMs = Date.now();
  store.nowMs = serverMs;
  emit();
}

/**
 * Force an immediate server-time re-sync via REST.
 * Fire-and-forget: the old base is still used until the fetch completes.
 */
export function forceResync(): void {
  fetchServerTimeMs().then(setBase).catch(() => {});
}

export function getServerNowMs(): number {
  return computeNowMs();
}

export function useServerTimeQuery() {
  return useQuery({
    queryKey: ['server', 'time'],
    queryFn: fetchServerTimeMs,
    refetchInterval: 15_000,
    retry: 2,
  });
}

/**
 * Call this ONCE near app root to keep the server-time base in sync.
 * (Avoid calling `useServerTimeQuery` in every component; it creates many observers.)
 */
export function useServerNowSync(): void {
  const q = useServerTimeQuery();
  React.useEffect(() => {
    if (typeof q.data === 'number') setBase(q.data);
  }, [q.data]);
}

export function useServerNow(): number {
  // React 18 has useSyncExternalStore; keep a tiny fallback for older.
  const useSyncExternalStore = (React as any).useSyncExternalStore as
    | undefined
    | ((sub: (cb: () => void) => () => void, get: () => number, getServer?: () => number) => number);

  if (useSyncExternalStore) {
    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  }

  const [now, setNow] = React.useState(getSnapshot());
  React.useEffect(() => subscribe(() => setNow(getSnapshot())), []);
  return now;
}

